import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Activity, Database, HeartPulse, Search, ShieldCheck } from 'lucide-react'
import clsx from 'clsx'
import { dbConsoleService } from '@/services/dbConsole'
import { DealLocator } from './db-search/DealLocator'
import { ExportVault } from './db-search/ExportVault'
import { HealthPanel } from './db-search/HealthPanel'
import { LiveLedger } from './db-search/LiveLedger'
import { TableBrowser } from './db-search/TableBrowser'

type TabId = 'locator' | 'ledger' | 'tables' | 'health' | 'exports'

const TABS: Array<{ id: TabId; label: string; hint: string; icon: typeof Search }> = [
  {
    id: 'locator',
    label: 'Deal Locator',
    hint: 'Find any deal since inception',
    icon: Search,
  },
  {
    id: 'ledger',
    label: 'Write Ledger',
    hint: 'Every row as it is saved, timestamped',
    icon: Activity,
  },
  {
    id: 'tables',
    label: 'Tables',
    hint: 'Raw records exactly as stored',
    icon: Database,
  },
  {
    id: 'health',
    label: 'Health',
    hint: 'Live engine and table diagnostics',
    icon: HeartPulse,
  },
  {
    id: 'exports',
    label: 'Export Vault',
    hint: 'Dual-authorized database export',
    icon: ShieldCheck,
  },
]

/**
 * DB Search — the Deal Locator plus the database-transparency console.
 *
 * The tabs after the first exist to answer "is my data really in there?"
 * without anyone having to take that on trust: watch rows land as they are
 * written, read them raw, check the engine's own health figures, and take a
 * checksummed copy out under two-person control.
 *
 * Panels mount lazily and unmount on switch, so the ledger's polling stops
 * when it is not on screen.
 */
const TAB_IDS: readonly TabId[] = ['locator', 'ledger', 'tables', 'health', 'exports']

export default function DBSearch() {
  // A link may name the tab and the export request (the bell's "Export waiting
  // for your approval" opens the request itself, T-42).
  const [params] = useSearchParams()
  const linkedTab = params.get('tab') as TabId | null
  const linkedRequest = params.get('request')
  const [tab, setTab] = useState<TabId>(linkedTab && TAB_IDS.includes(linkedTab) ? linkedTab : 'locator')
  useEffect(() => {
    if (linkedTab && TAB_IDS.includes(linkedTab)) setTab(linkedTab)
  }, [linkedTab, linkedRequest])

  // The console reads raw rows from every table and can bulk-export them, so it
  // is guarded by `database_console` — a governed permission no wildcard can
  // confer (app/core/rbac.py).
  //
  // Visibility is decided by ASKING THE API, not by inspecting a client-side
  // profile. This deployment has three auth modes (Entra, local JWT, dev
  // bypass) and the local profile is only populated for one of them, so
  // reading `localUser.permissions` hid the console from sessions the API was
  // happily serving. Probing the real endpoint cannot drift from enforcement in
  // either direction: a 403 hides the tabs, a 200 shows them. React Query
  // shares this result with the Health tab, so it costs no extra request.
  const consoleProbe = useQuery({
    queryKey: ['db-console', 'health'],
    queryFn: dbConsoleService.health,
    retry: false,
    // A person granted the console while this page is open (29 Oct 2026: Louis was
    // made admin mid-call) sees its tabs on the next focus or within a minute,
    // not only after a full reload. Once allowed, nothing re-polls.
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.status === 'error' ? 60_000 : false),
    staleTime: 30_000,
  })
  // Hidden until proven allowed — the safe default for a raw-data surface.
  const canOpenConsole = consoleProbe.isSuccess

  const visibleTabs = useMemo(
    () => (canOpenConsole ? TABS : TABS.filter((entry) => entry.id === 'locator')),
    [canOpenConsole],
  )

  // A tab that disappears underneath the selection must not leave a blank pane.
  const effectiveTab = visibleTabs.some((entry) => entry.id === tab) ? tab : 'locator'
  const active = visibleTabs.find((entry) => entry.id === effectiveTab) ?? TABS[0]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        aria-label="Database views"
        className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/10 px-3 pt-2"
      >
        {visibleTabs.map((entry) => {
          const Icon = entry.icon
          const selected = entry.id === effectiveTab
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              id={`db-tab-${entry.id}`}
              aria-selected={selected}
              aria-controls="db-tabpanel"
              title={entry.hint}
              onClick={() => setTab(entry.id)}
              className={clsx(
                'flex items-center gap-1.5 rounded-t-md border-b-2 px-3 py-1.5 text-[11px] font-medium transition',
                selected
                  ? 'border-amber-400 text-amber-100'
                  : 'border-transparent text-stone-500 hover:border-amber-400/25 hover:text-stone-200',
              )}
            >
              <Icon size={12} />
              {entry.label}
            </button>
          )
        })}
        <span className="ml-2 hidden text-[10px] text-stone-600 lg:inline">{active.hint}</span>
      </div>

      <div
        id="db-tabpanel"
        role="tabpanel"
        aria-labelledby={`db-tab-${effectiveTab}`}
        className="flex min-h-0 flex-1 flex-col"
      >
        {effectiveTab === 'locator' ? (
          <DealLocator />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col p-3">
            {effectiveTab === 'ledger' && <LiveLedger />}
            {effectiveTab === 'tables' && <TableBrowser />}
            {effectiveTab === 'health' && <HealthPanel />}
            {effectiveTab === 'exports' && <ExportVault focusRequestId={linkedRequest} />}
          </div>
        )}
      </div>
    </div>
  )
}
